import React, { useEffect } from 'react';

import { BulkEditIssueView } from './BulkEditIssueView';
import { BulkConfigureView } from './BulkConfigureView';
import { BulkCreateFlagsContainer } from './BulkCreateFlagsContainer';
import { BulkCreateDefaultView } from './BulkCreateDefaultView';
import type { Column, ColumnToFieldMapping } from './providers/BulkConfigureContextProvider';
import { useMappingsActions, useMappingsState } from './providers/BulkConfigureContextProvider';
import { useIssueCreateSidePanelContext } from './providers/IssueCreateSidePanelContextProvider';
import type { FieldMetadata } from './__types__/apiUtils';
import { useGetIssueTypeFields } from './utils/useGetIssueTypeFields';
import { mapTableToIssues } from './utils/tableUtils';
import { useBulkCreateContext } from './providers/BulkCreateContextProvider';

const createDefaultMappings = (
	columns: Column[],
	fields: FieldMetadata[],
): ColumnToFieldMapping[] => {
	const fieldMap: { [key: number]: string } = {
		0: 'summary',
		1: 'description',
	};
	return columns.slice(0, 2).map((column, index) => {
		const fieldId = fieldMap[index];
		const field = fields.find((f) => f.fieldId === fieldId);
		return { column, field };
	});
};

export const BulkNavigationContainer = ({ onClose }: { onClose: () => void }) => {
	useGetIssueTypeFields();
	const { optionsIssueTypeFields, optionsColumns, columnToFieldMappings } = useMappingsState();
	const { setColumnToFieldMappings, setOptionsColumns } = useMappingsActions();
	const [{ selectedTable }] = useIssueCreateSidePanelContext();
	const [, { setBulkIssuesList }] = useBulkCreateContext();

	useEffect(() => {
		if (selectedTable) {
			const columns: Column[] = [];

			// Determine initial columnIndex based on the presence of the numbered column
			let columnIndex = 0;
			const firstDataCell = selectedTable?.querySelector('tbody tr:first-child td');
			if (firstDataCell?.classList.contains('ak-renderer-table-number-column')) {
				columnIndex = 1;
			}

			// Always use the first row as the header row, even if it is not a HTML header row
			const firstRow = selectedTable?.querySelector('tbody tr:first-child');
			if (firstRow) {
				const firstRowCells = firstRow.querySelectorAll('td, th');
				firstRowCells.forEach((cell) => {
					columns.push({ index: columnIndex, header: cell.textContent ?? '' });
					columnIndex += 1;
				});
			}
			setOptionsColumns(columns);
		}
	}, [selectedTable, setOptionsColumns]);

	useEffect(() => {
		if (optionsIssueTypeFields && optionsColumns) {
			const defaultMappings = createDefaultMappings(optionsColumns, optionsIssueTypeFields);
			setColumnToFieldMappings(defaultMappings);
		}
	}, [optionsIssueTypeFields, optionsColumns, setColumnToFieldMappings]);

	useEffect(() => {
		if (columnToFieldMappings && columnToFieldMappings.length > 0 && selectedTable) {
			const mappedIssues = mapTableToIssues(columnToFieldMappings, selectedTable);
			void setBulkIssuesList(mappedIssues);
		}
	}, [columnToFieldMappings, selectedTable, setBulkIssuesList]);

	return (
		<>
			<BulkCreateDefaultView onClose={onClose} />
			<BulkConfigureView />
			<BulkEditIssueView />
			<BulkCreateFlagsContainer />
		</>
	);
};
